/* eslint-disable i18next/no-literal-string */
import { Box } from '@/components/muiComponents';
import { LOGIN_SESSION_IN_PROGRESS } from '@/constants/login';
import { LOG_MESSAGES } from '@/constants/logMessages';
import { logEvent, logger } from '@/util/logger';
import { Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useOktaAuth } from '@okta/okta-react';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AutoLoginUI from './components/AutoLoginUI';
import ManualLoginUI from './components/ManualLoginUI';
import PasswordFormUI from './components/PasswordFormUI';

interface DemoLoginProps {
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

export const DemoLogin: React.FC<DemoLoginProps> = ({ onSuccess, onError }) => {
  const { oktaAuth } = useOktaAuth();
  const theme = useTheme();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const demoEmail = process.env.REACT_APP_DEMO_EMAIL as string;
  const demoPassword = process.env.REACT_APP_DEMO_PASSWORD as string;
  const agentEmail = process.env.REACT_APP_AGENT_EMAIL as string;

  // Check if user manually logged out
  const wasManualLogout = localStorage.getItem('demoManualLogout') === 'true';

  // Determine if we should auto-login (only if not manual logout and demo password exists)
  const shouldAutoLogin = Boolean(demoPassword) && !wasManualLogout;

  const performLogin = useCallback(
    async (loginPassword: string) => {
      setLoading(true);
      setError('');

      localStorage.setItem(LOGIN_SESSION_IN_PROGRESS, 'true');

      try {
        logEvent(LOG_MESSAGES.USER_ATTEMPTED_LOGIN);

        const transaction = await oktaAuth.signInWithCredentials({
          username: demoEmail,
          password: loginPassword,
        });

        if (transaction.status === 'SUCCESS') {
          // Clear the manual logout flag on successful login
          localStorage.removeItem('demoManualLogout');

          // Get session token and redirect
          await oktaAuth.signInWithRedirect({
            sessionToken: transaction.sessionToken,
          });

          // Only navigate if we're not already being redirected by Okta
          if (window.location.pathname === '/demo/login') {
            navigate('/');
          }
          onSuccess?.();
        } else {
          // Handle MFA or other auth states
          setError(
            'Additional authentication required. Please use the standard login.',
          );
          localStorage.removeItem(LOGIN_SESSION_IN_PROGRESS);
        }
      } catch (error: any) {
        console.error('Demo login failed:', error);
        localStorage.removeItem(LOGIN_SESSION_IN_PROGRESS);
        logger.error({
          message: LOG_MESSAGES.LOGIN_FAILED,
          error,
        });

        const errorMessage =
          error?.errorSummary ||
          error?.message ||
          'Invalid credentials. Please try again.';
        setError(errorMessage);
        onError?.(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [demoEmail, oktaAuth, navigate, onSuccess, onError],
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!password.trim()) {
        setError('Password is required');
        return;
      }
      await performLogin(password.trim());
    },
    [password, performLogin],
  );

  const handleManualLogin = useCallback(async () => {
    if (demoPassword) {
      await performLogin(demoPassword);
    }
  }, [demoPassword, performLogin]);

  // Auto-login effect when demo password is available and not manual logout
  useEffect(() => {
    if (shouldAutoLogin && demoPassword) {
      performLogin(demoPassword);
    }
  }, [shouldAutoLogin, demoPassword, performLogin]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundColor: theme.palette.background.default,
        padding: 3,
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 400,
          padding: 4,
          backgroundColor: theme.palette.background.paper,
          borderRadius: 2,
          boxShadow: theme.shadows[3],
        }}
      >
        <Box sx={{ textAlign: 'center', marginBottom: 3 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Demo Platform
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Welcome to the demo environment
          </Typography>
        </Box>

        {shouldAutoLogin ? (
          <AutoLoginUI demoEmail={agentEmail} error={error} />
        ) : wasManualLogout && demoPassword ? (
          <ManualLoginUI
            demoEmail={agentEmail}
            handleManualLogin={handleManualLogin}
            loading={loading}
            error={error}
          />
        ) : (
          <PasswordFormUI
            demoEmail={agentEmail}
            handleSubmit={handleSubmit}
            loading={loading}
            error={error}
            password={password}
            setPassword={setPassword}
          />
        )}
      </Box>
    </Box>
  );
};
